import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { streamChat } from "../api/ai.api";
import {
  addToolCallToLastAssistant,
  appendAssistantDelta,
  appendMessage,
  finalizeLastAssistant,
  setError,
  setStreaming,
  setUsage,
  updateToolCallResult,
} from "../stores/aiChatSlice";
import { AIChatMessage, AIWireMessage } from "../types/ai.types";
import { applyAutomationDraft, draftOf } from "../automationDraft/applyAutomationDraft";

/** What `send` resolves with once the stream has fully closed. */
export interface SendResult {
  /** false when the turn errored or was cancelled mid-stream. */
  ok: boolean;
  /** The assistant's complete reply text (markdown) for this turn. */
  text: string;
}

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Hook that owns send-flow + stream handling. The Composer calls `send(text)`,
 * messages stream into the slice, and `cancel()` aborts mid-stream.
 *
 * We rebuild the wire-shape `AIWireMessage[]` from the slice on each send,
 * because the BE is stateless and needs the full thread. Tool-result entries
 * are folded back in so the model has full context across iterations.
 */
export function useAIChat() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { messages, currentModelId, isStreaming, language } = useAppSelector(
    (s) => s.aiChat
  );
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (text: string): Promise<SendResult> => {
      const trimmed = text.trim();
      if (!trimmed || !currentModelId || isStreaming) {
        return { ok: false, text: "" };
      }
      // Local copy of the streamed reply so callers (voice mode) can act on
      // the finished text without re-reading the store.
      let replyText = "";
      let failed = false;

      const userMsg: AIChatMessage = {
        id: uid(),
        role: "user",
        content: trimmed,
        createdAt: Date.now(),
      };
      const assistantMsg: AIChatMessage = {
        id: uid(),
        role: "assistant",
        content: "",
        toolCalls: [],
        isStreaming: true,
        createdAt: Date.now(),
      };
      dispatch(appendMessage(userMsg));
      dispatch(appendMessage(assistantMsg));
      dispatch(setStreaming(true));
      dispatch(setError(null));

      // Wire-shape thread — drop UI-only fields, stamp the new user msg.
      const wire: AIWireMessage[] = [
        ...messages.flatMap<AIWireMessage>((m) => {
          if (m.role === "user") return [{ role: "user", content: m.content }];
          if (m.role === "assistant") {
            // For prior turns include the assistant text. Tool-call records
            // were already echoed back into the thread as tool_result entries
            // by the stream loop below, so we don't re-include them here.
            return m.content
              ? [{ role: "assistant", content: m.content }]
              : [];
          }
          return [];
        }),
        { role: "user", content: trimmed },
      ];

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await streamChat({
          modelId: currentModelId,
          messages: wire,
          language,
          signal: controller.signal,
          onEvent: (e) => {
            switch (e.type) {
              case "text_delta":
                replyText += e.text;
                dispatch(appendAssistantDelta(e.text));
                break;
              case "tool_use":
                dispatch(
                  addToolCallToLastAssistant({
                    id: e.id,
                    name: e.name,
                    input: e.input,
                  })
                );
                break;
              case "tool_result":
                dispatch(updateToolCallResult({ id: e.id, result: e.result }));
                // A validated automation draft builds itself in the builder
                // while the answer is still streaming. Unsaved — the user Saves.
                {
                  const draft = draftOf({ id: e.id, name: e.name, result: e.result });
                  if (draft) void applyAutomationDraft(draft, dispatch, navigate);
                }
                break;
              case "usage":
                dispatch(
                  setUsage({
                    inputTokens: e.inputTokens,
                    outputTokens: e.outputTokens,
                    cachedTokens: e.cachedTokens,
                  })
                );
                break;
              case "error":
                failed = true;
                dispatch(setError(e.message));
                break;
              case "done":
                dispatch(finalizeLastAssistant());
                dispatch(setStreaming(false));
                break;
            }
          },
        });
      } catch (err: any) {
        failed = true;
        if (err?.name !== "AbortError") {
          dispatch(setError(err?.message ?? "Chat failed"));
        }
        dispatch(finalizeLastAssistant());
        dispatch(setStreaming(false));
      } finally {
        abortRef.current = null;
      }
      return { ok: !failed, text: replyText };
    },
    [currentModelId, dispatch, navigate, isStreaming, messages, language]
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { send, cancel };
}
